import { useState } from "react";
import { cn } from "../../lib/utils.js";

interface Props {
  onClose: () => void;
}

const ENV_VARS = [
  { key: "ANTHROPIC_API_KEY", label: "Anthropic API Key", placeholder: "sk-ant-…", provider: "Claude" },
  { key: "OPENAI_API_KEY", label: "OpenAI API Key", placeholder: "sk-…", provider: "OpenAI / Gemini" },
  { key: "GEMINI_API_KEY", label: "Gemini API Key (optional)", placeholder: "AI…", provider: "Gemini" },
];

export function SettingsPanel({ onClose }: Props) {
  const [tab, setTab] = useState<"keys" | "skills" | "about">("keys");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-[560px] max-h-[80vh] rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">Settings</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="1" y1="1" x2="11" y2="11" /><line x1="11" y1="1" x2="1" y2="11" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b border-[var(--border)]">
          {(["keys", "skills", "about"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-3 py-1.5 text-xs rounded-t-lg capitalize transition-colors border-b-2 -mb-px",
                tab === t
                  ? "border-[var(--accent)] text-[var(--accent)]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              )}
            >
              {t === "keys" ? "API Keys" : t === "skills" ? "Skills" : "About"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "keys" && (
            <div className="space-y-4">
              <p className="text-xs text-[var(--text-secondary)]">
                API keys are loaded from <code className="text-[var(--accent)]">.env</code> in the repo root.
                Restart the engine after editing.
              </p>
              <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">Provider</th>
                      <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">Env Var</th>
                      <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">Format</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ENV_VARS.map((v) => (
                      <tr key={v.key} className="border-b border-[var(--border)] last:border-0">
                        <td className="px-3 py-2 text-[var(--text-primary)]">{v.provider}</td>
                        <td className="px-3 py-2 font-mono text-[var(--accent)]">{v.key}</td>
                        <td className="px-3 py-2 text-[var(--text-secondary)]">{v.placeholder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] p-3">
                <p className="text-xs font-mono text-[var(--text-secondary)] whitespace-pre">{`# legalgraph-cowork/.env\nANTHROPIC_API_KEY=sk-ant-...\nOPENAI_API_KEY=sk-...\nGEMINI_API_KEY=AI...`}</p>
              </div>
            </div>
          )}

          {tab === "skills" && (
            <div className="space-y-3">
              <p className="text-xs text-[var(--text-secondary)]">
                Skills are markdown files that set Claude's system prompt for a task.
                Type <code className="text-[var(--accent)]">/skill-name</code> in the chat input to use one.
              </p>
              <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] p-3 space-y-2">
                <p className="text-xs font-semibold text-[var(--text-primary)]">Custom skills directory</p>
                <code className="block text-xs font-mono text-[var(--accent)] bg-[var(--bg-tertiary)] px-2 py-1.5 rounded">
                  ~/.cowork/skills/
                </code>
                <p className="text-xs text-[var(--text-secondary)]">
                  Create <code className="text-[var(--accent)]">~/.cowork/skills/</code> and add any <code>.md</code> file.
                  The filename becomes the skill name (e.g. <code>legal-review.md</code> → <code>/legal-review</code>).
                </p>
              </div>
              <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] p-3">
                <p className="text-xs font-semibold text-[var(--text-primary)] mb-2">Example: <code className="text-[var(--accent)]">~/.cowork/skills/legal-review.md</code></p>
                <pre className="text-[11px] font-mono text-[var(--text-secondary)] whitespace-pre-wrap">{`# Legal Review

Review the provided document for:
1. Contractual obligations and liabilities
2. Ambiguous or problematic clauses
3. Missing standard protections
4. Jurisdiction-specific issues

Flag CRITICAL, HIGH, and MEDIUM risks.`}</pre>
              </div>
              <p className="text-xs text-[var(--text-secondary)]">
                Bundled skills: <code className="text-[var(--accent)]">summarize</code>, <code className="text-[var(--accent)]">code-review</code>, <code className="text-[var(--accent)]">plan</code>, <code className="text-[var(--accent)]">debug</code>, <code className="text-[var(--accent)]">draft-email</code>
              </p>
            </div>
          )}

          {tab === "about" && (
            <div className="space-y-3 text-xs text-[var(--text-secondary)]">
              <p className="text-[var(--text-primary)] font-semibold">AI CoWork v0.1.0</p>
              <p>Multi-model AI assistant for knowledge workers. Supports Claude, GPT-4, Gemini, and local Ollama models.</p>
              <div className="rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] p-3 space-y-1 font-mono text-[11px]">
                <div className="flex justify-between"><span>Engine</span><span className="text-[var(--accent)]">ws://localhost:3001</span></div>
                <div className="flex justify-between"><span>Reporting</span><span className="text-[var(--accent)]">http://localhost:3002</span></div>
                <div className="flex justify-between"><span>Skills dir</span><span className="text-[var(--accent)]">~/.cowork/skills/</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
